import React from 'react';
import { ExternalLink, FileText } from 'lucide-react';

const STATUS_STYLES = {
    paid: { color: '#86EFAC', bg: 'rgba(34, 197, 94, 0.14)' },
    open: { color: '#FCD34D', bg: 'rgba(245, 158, 11, 0.14)' },
    failed: { color: '#FCA5A5', bg: 'rgba(239, 68, 68, 0.14)' },
    void: { color: '#94A3B8', bg: 'rgba(148, 163, 184, 0.14)' },
    refunded: { color: '#93C5FD', bg: 'rgba(59, 130, 246, 0.14)' },
};

function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(cents, currency = 'usd') {
    if (cents == null) return '';
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(dollars);
}

function capitalizeFirst(str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function PaymentHistoryTable({ paymentHistory }) {
    if (!paymentHistory || paymentHistory.length === 0) {
        return (
            <section style={styles.section}>
                <h3 style={styles.sectionTitle}>Payment history</h3>
                <div style={styles.emptyState}>
                    <FileText size={24} style={{ color: '#64748B', marginBottom: 8 }} />
                    <div>No payments yet.</div>
                </div>
            </section>
        );
    }

    return (
        <section style={styles.section}>
            <h3 style={styles.sectionTitle}>Payment history</h3>
            <div style={styles.tableWrapper}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Date</th>
                            <th style={styles.th}>Description</th>
                            <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                            <th style={styles.th}>Status</th>
                            <th style={{ ...styles.th, textAlign: 'right' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paymentHistory.map((invoice, idx) => {
                            const statusStyle = STATUS_STYLES[invoice.status] || STATUS_STYLES.void;
                            return (
                                <tr key={invoice.invoice_id || idx} style={styles.row}>
                                    <td style={styles.td}>{formatDate(invoice.date)}</td>
                                    <td style={styles.td}>{invoice.description || 'Subscription payment'}</td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>
                                        {formatCurrency(invoice.amount_paid_cents ?? invoice.amount_cents, invoice.currency)}
                                    </td>
                                    <td style={styles.td}>
                                        <span style={{
                                            ...styles.statusBadge,
                                            color: statusStyle.color,
                                            background: statusStyle.bg,
                                        }}>
                                            {capitalizeFirst(invoice.status)}
                                        </span>
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'right' }}>
                                        {invoice.hosted_invoice_url && (
                                            <a
                                                href={invoice.hosted_invoice_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={styles.link}
                                            >
                                                View invoice
                                                <ExternalLink size={12} style={{ marginLeft: 4 }} />
                                            </a>
                                        )}
                                        {invoice.status === 'open' && invoice.hosted_invoice_url && (
                                            <a
                                                href={invoice.hosted_invoice_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ ...styles.link, marginLeft: 12, color: '#FCD34D' }}
                                            >
                                                Pay
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

const styles = {
    section: {
        marginBottom: 24,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 600,
        color: '#CBD5E1',
        margin: '0 0 12px',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px',
        color: '#B4C0D3',
        fontSize: 14,
        background: '#0F172A',
        border: '1px solid rgba(148, 163, 184, 0.12)',
        borderRadius: 8,
    },
    tableWrapper: {
        overflowX: 'auto',
        background: '#0F172A',
        border: '1px solid rgba(148, 163, 184, 0.12)',
        borderRadius: 8,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 13,
    },
    th: {
        textAlign: 'left',
        padding: '12px 16px',
        color: '#A3B1C6',
        fontWeight: 600,
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: '0.02em',
        borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
    },
    row: {
        borderBottom: '1px solid rgba(148, 163, 184, 0.08)',
    },
    td: {
        padding: '12px 16px',
        color: '#E2E8F0',
        verticalAlign: 'middle',
    },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
    },
    link: {
        display: 'inline-flex',
        alignItems: 'center',
        color: '#60A5FA',
        textDecoration: 'none',
        fontSize: 12,
        fontWeight: 500,
    },
};
